import React from 'react';
import { WalletTransaction, TransactionType } from '@/types/wallet.types';

interface TransactionListProps {
  transactions: WalletTransaction[];
  isLoading?: boolean;
}

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTypeConfig = (type: TransactionType): { color: string; icon: React.ReactNode; prefix: string } => {
  switch (type) {
    case 'CREDIT':
      return {
        color: 'text-green-600 bg-green-100',
        prefix: '+',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
          </svg>
        ),
      };
    case 'DEBIT':
      return {
        color: 'text-red-600 bg-red-100',
        prefix: '-',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
          </svg>
        ),
      };
    case 'REFUND':
      return {
        color: 'text-blue-600 bg-blue-100',
        prefix: '+',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        ),
      };
  }
};

const TransactionItem: React.FC<{ transaction: WalletTransaction }> = ({ transaction }) => {
  const config = getTypeConfig(transaction.type);

  return (
    <div className="flex items-center gap-4 py-4 border-b border-secondary-100 last:border-b-0">
      {/* Icon */}
      <div className={`p-2 rounded-full ${config.color}`}>
        {config.icon}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-secondary-900 truncate">
          {transaction.description}
        </p>
        <p className="text-xs text-secondary-500 mt-0.5">
          {transaction.reference_type} | {formatDate(transaction.created_at)}
        </p>
      </div>

      {/* Amount and Balance */}
      <div className="text-right">
        <p className={`text-sm font-semibold ${transaction.type === 'DEBIT' ? 'text-red-600' : 'text-green-600'}`}>
          {config.prefix}{formatIndianCurrency(transaction.amount)}
        </p>
        <p className="text-xs text-secondary-500 mt-0.5">
          Bal: {formatIndianCurrency(transaction.balance_after)}
        </p>
      </div>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-4 py-4 border-b border-secondary-100 animate-pulse">
        <div className="w-10 h-10 bg-secondary-200 rounded-full"></div>
        <div className="flex-1">
          <div className="h-4 bg-secondary-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-secondary-100 rounded w-1/2"></div>
        </div>
        <div className="text-right">
          <div className="h-4 bg-secondary-200 rounded w-16 mb-2"></div>
          <div className="h-3 bg-secondary-100 rounded w-20"></div>
        </div>
      </div>
    ))}
  </div>
);

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  isLoading = false,
}) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-4">
          <svg
            className="w-6 h-6 text-secondary-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <p className="text-secondary-600">No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-secondary-100">
      {transactions.map((transaction) => (
        <TransactionItem key={transaction.id} transaction={transaction} />
      ))}
    </div>
  );
};

export default TransactionList;
